import get from 'lodash/get'
import omit from 'lodash/omit'
import { createReducer } from 'redux-create-reducer'
import type { Track, TrackId } from 'types/track'
import {
  DATA_LOAD,
  GROUP_DELETE_SUCCESS,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PLAYLIST_LOAD_BATCH,
  TRACK_ADD_WRITER_SUCCESS,
  TRACK_ALBUM_ARTWORK_UPDATE,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_DELETE_WRITER_SUCCESS,
  TRACK_DURATION_UPDATE,
  TRACK_FORMAT_DELETE_SUCCESS,
  TRACK_LOAD_BATCH,
  TRACK_MERGE_SUCCESS,
  TRACK_METADATA_COMMENT_DELETE_SUCCESS,
  TRACK_METADATA_COMMENT_EDIT_SUCCESS,
  TRACK_METADATA_COMMENT_SAVE_SUCCESS,
  TRACK_SHARE_SUCCESS,
  TRACK_UPDATE_WRITER_SUCCESS,
  TRACKS_UPDATE,
  TRACK_SHARES_LOADING,
  TRACK_SHARES_LOADING_ERROR,
  TRACK_SHARES_LOADING_SUCCESS,
} from '@disco/constants/ActionTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { removeGroupFromEntities } from '@disco/utils/immutable'
import { isAlbumUnknown, isArtistUnknown } from '@disco/utils/string-utils'
const initialState: Record<TrackId, Track> = {}

function updateShares(state, id, updater) {
  return {
    ...state,
    ...{
      [id]: {
        ...state[id],
        shares: updater(state[id].shares),
      },
    },
  }
}

function processUpdate(state, pendingUpdate, resetPlaylistInfo = true) {
  const update = {}
  pendingUpdate.forEach(newTrack => {
    const id = newTrack.id
    let normalizedTrack

    if (resetPlaylistInfo) {
      const playlists = newTrack.playlist_versions
        ? newTrack.playlist_versions.map(playlist => playlist.playlist_id)
        : []
      normalizedTrack = {
        ...omit(newTrack, ['playlist_versions']),
        // relies on the fact that only one version of the playlist exists
        // and we can reference it by playlist id instead of version
        playlists: newTrack.playlists || playlists,
      }
    } else {
      normalizedTrack = newTrack
    }

    // server sometimes zero duration for tracks, when user starts playback
    // player override duration with proper value if it's possible,
    // we have to preserve correct duration, so further server updates
    // won't override it with wrong value
    const duration =
      !state[id] || (normalizedTrack.duration && normalizedTrack.duration > 0)
        ? normalizedTrack.duration
        : state[id].duration
    const mergedTrack = { ...(state[id] || {}), ...normalizedTrack, duration }
    // handle artwork removal
    update[id] = normalizedTrack.artwork
      ? mergedTrack
      : omit(mergedTrack, ['artwork', 'artwork_sizes'])
  })
  return { ...state, ...update }
}

export type State = typeof initialState
export const tracksReducer = createReducer<State, any>(initialState, {
  [DATA_LOAD](state, { data }) {
    if (!data.tracks || !data.tracks.length) return state
    return processUpdate(state, data.tracks)
  },

  [TRACK_LOAD_BATCH](state, action) {
    // exit early if we don't have anything to do
    if (!action.list || action.list.length === 0) return state
    // ensure that params consists of tracks only
    const pendingUpdate = action.list.filter(item => item.type === TRACK)
    return processUpdate(state, pendingUpdate, action.resetPlaylistInfo)
  },

  // track reducer inspect contents of the playlists to see if there is
  // track info
  [PLAYLIST_LOAD_BATCH](state, action) {
    // exit early if we don't have anything to do
    if (!action.list || action.list.length === 0) return state
    const pendingUpdate: any[] = []
    action.list.forEach(playlist => {
      if (playlist.tracks) pendingUpdate.push(...playlist.tracks)
    })
    // TODO: playlist return tracks without playlist_versions info, but we have to
    // ensure that track will be updated with playlist state, whether it was
    // removed or added to playlist
    // NOTE: might be useless to try to keep playlists prop updated due to
    // pagination, we don't know about full playlist state
    return processUpdate(state, pendingUpdate, false)
  },

  [TRACK_DELETE_BATCH_SUCCESS](state, action) {
    return omit(state, action.trackIds)
  },

  [TRACK_MERGE_SUCCESS](state, { tracksToMerge }) {
    return omit(
      state,
      tracksToMerge.map(t => t.id),
    )
  },

  [GROUP_DELETE_SUCCESS](state, { group }) {
    return removeGroupFromEntities(state, group)
  },

  [TRACK_FORMAT_DELETE_SUCCESS](state, { track, format }) {
    const update = {}
    Object.keys(state).forEach(prop => {
      const trackId = Number(prop)
      // trackId is from Object.keys(state), so state[trackId] is defined
      const t = state[trackId]!
      if (t.id === track.id) {
        const all_files = { ...t.all_files }
        delete all_files[format]
        update[trackId] = { ...t, all_files }
      }
    })
    return { ...state, ...update }
  },

  [PLAYLIST_DELETE_BATCH_SUCCESS](state, { playlistIds }) {
    const update = {}
    Object.keys(state).forEach(prop => {
      const trackId = Number(prop)
      // trackId is from Object.keys(state), so state[trackId] is defined
      const t = state[trackId]!

      if (t.playlists && t.playlists.some(id => playlistIds.indexOf(id) > -1)) {
        const playlists = t.playlists.filter(id => {
          return playlistIds.indexOf(id) === -1
        })
        update[trackId] = { ...t, playlists: playlists }
      }
    })
    return { ...state, ...update }
  },

  [TRACK_METADATA_COMMENT_SAVE_SUCCESS](
    state,
    { trackId, comment, isBatchComment },
  ) {
    if (isBatchComment) return state
    if (!state[trackId]) return state
    const original = state[trackId]!
    const originalComments = get(original, 'comments', [])
    const update = {
      [trackId]: { ...original, comments: [comment, ...originalComments] },
    }
    return { ...state, ...update }
  },

  [TRACK_METADATA_COMMENT_EDIT_SUCCESS](state, { trackId, index, comment }) {
    if (!state[trackId]) return state
    const original = state[trackId]!
    const originalComments = get(original, 'comments', [])
    const update = {
      [trackId]: {
        ...original,
        comments: [
          ...originalComments.slice(0, index),
          comment,
          ...originalComments.slice(index + 1),
        ],
      },
    }
    return { ...state, ...update }
  },

  [TRACK_METADATA_COMMENT_DELETE_SUCCESS](
    state,
    { trackId, commentId: commentIdToDelete },
  ) {
    if (!state[trackId]) return state
    const original = state[trackId]!
    const update = {
      [trackId]: {
        ...original,
        comments: (original.comments ?? []).filter(comment => {
          const commentId = comment.id

          if (commentId) {
            return commentId !== commentIdToDelete
          } else {
            return true
          }
        }),
      },
    }
    return { ...state, ...update }
  },

  [TRACK_ALBUM_ARTWORK_UPDATE](
    state,
    { artist, album, artwork, artwork_sizes },
  ) {
    const isArtistNotSet = isArtistUnknown(artist)
    const isAlbumNotSet = isAlbumUnknown(album)
    if (isArtistNotSet || isAlbumNotSet) return state
    const update = {}
    Object.keys(state).forEach(prop => {
      const trackId = Number(prop)
      // trackId is from Object.keys(state), so state[trackId] is defined
      const t = state[trackId]!

      if (t.album === album && t.artist === artist) {
        if (artwork) {
          update[trackId] = { ...t, artwork, artwork_sizes }
        } else {
          update[trackId] = omit(t, ['artwork', 'artwork_sizes'])
        }
      }
    })
    return { ...state, ...update }
  },

  [TRACK_DURATION_UPDATE](state, { id, duration }) {
    if (!state[id]) return state
    const update = {
      [id]: { ...state[id]!, duration },
    }
    return { ...state, ...update }
  },

  [TRACK_DELETE_WRITER_SUCCESS](state, { trackId, splitId }) {
    if (!trackId || !state[trackId]) return state
    const t = state[trackId]!
    const update = {
      [trackId]: {
        ...t,
        track_writers: (t.track_writers ?? []).filter(s => s.id !== splitId),
      },
    }
    return { ...state, ...update }
  },

  [TRACK_ADD_WRITER_SUCCESS](state, { track, writer }) {
    // case of 2+ tracks: API returns "OK" and no ID
    if (!track) return state
    const t = state[track]
    // case of 1 track in Edit Multiple: no point to update
    if (!t?.track_writers) return state
    const update = {
      [track]: {
        ...t,
        track_writers: [...t.track_writers, writer],
      },
    }
    return { ...state, ...update }
  },

  [TRACK_SHARE_SUCCESS](state, { track, result }) {
    const trackId = track.id
    const resultId = result.id

    return updateShares(state, trackId, shares => ({
      ...shares,
      items: [resultId].concat((shares && shares.items) || []),
    }))
  },

  [TRACK_SHARES_LOADING](state, { id }) {
    return updateShares(state, id, shares => ({
      ...shares,
      error: null,
      errorMessage: null,
      loading: true,
      loaded: false,
    }))
  },

  [TRACK_SHARES_LOADING_SUCCESS](state, { id, responseShares }) {
    return updateShares(state, id, shares => ({
      ...shares,
      items: responseShares.map(item => item.id),
      error: null,
      errorMessage: null,
      loading: false,
      loaded: true,
    }))
  },

  [TRACK_SHARES_LOADING_ERROR](state, { id, error, errorMessage }) {
    return updateShares(state, id, shares => ({
      ...shares,
      error,
      errorMessage,
      loading: false,
      loaded: false,
    }))
  },

  [TRACK_UPDATE_WRITER_SUCCESS](state, { track, split }) {
    const t = state[track]
    if (!track || !t?.track_writers) return state
    const splitIndex = t.track_writers.findIndex(w => w.id === split.id)
    const update = {
      [track]: {
        ...t,
        track_writers: [
          ...t.track_writers.slice(0, splitIndex),
          split,
          ...t.track_writers.slice(splitIndex + 1),
        ],
      },
    }
    return { ...state, ...update }
  },

  [TRACKS_UPDATE](state, { tracks }) {
    if (tracks.length === 0) {
      return state
    }

    const update = {}
    tracks.forEach(track => {
      update[track.id] = { ...state[track.id], ...track }
    })
    return { ...state, ...update }
  },
})
